import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * usePushNotifications — manages the user's Web Push subscription.
 *
 * Returns:
 *   supported  — boolean. The browser exposes ServiceWorker + PushManager
 *                + Notification, AND the backend confirmed it has VAPID
 *                keys. Hide the toggle when this is false.
 *   enabled    — boolean. True when both the browser has an active
 *                subscription AND we've registered it with the backend.
 *   loading    — boolean. UI should show a spinner / disable the toggle.
 *   error      — last error string (e.g. "Notifications blocked").
 *   subscribe()
 *   unsubscribe()
 *
 * Permission prompt is ONLY triggered by subscribe() — never on mount.
 */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function browserSupportsPush() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window &&
    window.isSecureContext
  );
}

export function usePushNotifications() {
  const [supported, setSupported] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [vapidKey, setVapidKey] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!browserSupportsPush()) {
          if (!cancelled) {
            setSupported(false);
            setLoading(false);
          }
          return;
        }
        // Confirm the backend has VAPID configured and grab the key.
        const { data } = await api.get("/push/vapid-public-key");
        if (cancelled) return;
        if (!data?.enabled || !data?.public_key) {
          setSupported(false);
          setLoading(false);
          return;
        }
        setVapidKey(data.public_key);
        setSupported(true);
        // Reflect the current browser subscription state so the toggle
        // matches reality on page reload.
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setEnabled(!!sub);
      } catch (e) {
        if (!cancelled) {
          console.warn("[push] init failed:", e?.message || e);
          setSupported(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const subscribe = useCallback(async () => {
    if (!supported || !vapidKey) return;
    setLoading(true);
    setError("");
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setError(perm === "denied"
          ? "Notifications blocked. Re-enable in your browser settings to receive Live alerts."
          : "Notifications not enabled.");
        setEnabled(false);
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
      }
      await api.post("/push/subscribe", {
        subscription: sub.toJSON(),
        user_agent: navigator.userAgent,
      });
      setEnabled(true);
    } catch (e) {
      console.warn("[push] subscribe failed:", e?.message || e);
      setError("Could not enable phone alerts.");
    } finally {
      setLoading(false);
    }
  }, [supported, vapidKey]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    setError("");
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        try {
          await api.post("/push/unsubscribe", { endpoint });
        } catch (e) {
          // Server-side prune happens on next failed send anyway.
        }
      }
      setEnabled(false);
    } catch (e) {
      console.warn("[push] unsubscribe failed:", e?.message || e);
      setError("Could not turn off phone alerts.");
    } finally {
      setLoading(false);
    }
  }, [supported]);

  return { supported, enabled, loading, error, subscribe, unsubscribe };
}
